import React, { useEffect, useState } from 'react';
import { Download, Share, Plus, X } from 'lucide-react';
import { Button } from '../ui/Button';

const DISMISS_UNTIL_KEY = 'install-banner-dismissed-until';
const SESSION_DISMISS_KEY = 'install-banner-dismissed-session';
const DISMISS_DAYS = 30;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  const uaMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const narrow = window.matchMedia('(max-width: 900px)').matches;
  return uaMobile || narrow;
};

const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS Safari exposes navigator.standalone when launched from the home screen.
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
};

const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const iOSUA = /iPhone|iPad|iPod/i.test(ua);
  // iPadOS 13+ reports as Mac; detect touch + Mac as an iPad fallback.
  const iPadOS = ua.includes('Mac') && 'ontouchend' in document;
  return iOSUA || iPadOS;
};

const dismissedFor30Days = (): boolean => {
  try {
    const until = localStorage.getItem(DISMISS_UNTIL_KEY);
    if (!until) return false;
    return Date.now() < parseInt(until, 10);
  } catch {
    return false;
  }
};

const dismissedThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

export const InstallAppBanner: React.FC = () => {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [iosInstructions, setIosInstructions] = useState(false);

  useEffect(() => {
    if (!isMobileViewport()) return;
    if (isStandalone()) return;
    if (dismissedFor30Days()) return;
    if (dismissedThisSession()) return;

    if (isIOS()) {
      // iOS Safari never fires beforeinstallprompt — surface manual instructions.
      setIosInstructions(true);
      setVisible(true);
      return;
    }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };

    const onInstalled = () => {
      setVisible(false);
      setPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!prompt) return;
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === 'accepted') {
        setVisible(false);
      }
    } finally {
      setPrompt(null);
    }
  };

  const handleDontRemind = () => {
    try {
      localStorage.setItem(
        DISMISS_UNTIL_KEY,
        String(Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000),
      );
    } catch {
      // Ignore storage errors.
    }
    setVisible(false);
  };

  const handleSessionDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, '1');
    } catch {
      // Ignore storage errors.
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center">
            <Download size={18} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">Install Fulcrum on your phone</h3>
          {iosInstructions ? (
            <p className="text-sm text-gray-600 mt-1">
              Tap <Share size={14} className="inline -mt-0.5" aria-label="Share" /> Share, then{' '}
              <span className="whitespace-nowrap">
                <Plus size={14} className="inline -mt-0.5" aria-label="Add" /> Add to Home Screen
              </span>{' '}
              for faster access and notifications.
            </p>
          ) : (
            <p className="text-sm text-gray-600 mt-1">
              Add the app to your home screen for faster access and native-like navigation.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {!iosInstructions && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleInstall}
                className="flex items-center gap-2"
              >
                <Download size={16} />
                Install
              </Button>
            )}
            <button
              type="button"
              onClick={handleDontRemind}
              className="text-sm text-gray-600 hover:text-gray-900 px-2 py-1"
            >
              Don&apos;t remind me
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSessionDismiss}
          className="shrink-0 p-1 text-gray-400 hover:text-gray-600"
          aria-label="Dismiss"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};
